// Time formatting for reports. The zone is always supplied by the API
// (`meta.timezone`) — the SPA never picks one itself and never falls back to
// the browser's, because the customer's rule is one system zone everywhere.

/** Seconds → "h:mm:ss" (always shows hours, so columns line up). */
export function fmtHms(totalSeconds: number): string {
  const s = Math.max(0, Math.round(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${h}:${m.toString().padStart(2, "0")}:${sec.toString().padStart(2, "0")}`;
}

/** Seconds → "m:ss" for short durations (gaps, holds). */
export function fmtMs(totalSeconds: number | null | undefined): string {
  if (totalSeconds === null || totalSeconds === undefined) return "—";
  const s = Math.max(0, Math.round(totalSeconds));
  return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;
}

/** An ISO instant rendered as a wall-clock time in `tz`. */
export function fmtTimeInZone(iso: string | null | undefined, tz: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  }).format(d);
}

/** An ISO instant rendered as date + time in `tz`. */
export function fmtDateTimeInZone(iso: string | null | undefined, tz: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(d);
}

/** Today's (or an offset day's) calendar date in `tz`, as YYYY-MM-DD. */
export function localDateInZone(tz: string, daysAgo = 0, now: Date = new Date()): string {
  const shifted = new Date(now.getTime() - daysAgo * 86_400_000);
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(shifted);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

/** Hour of day (0-23) → "9 AM", "12 PM", "11 PM". */
export function fmtHour(hour: number): string {
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${h12} ${hour < 12 ? "AM" : "PM"}`;
}
